import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {MinimalSchemaTag} from 'folder-items/FileConstants';
import {BaseTypedValueType, Cell} from 'notebook/tables/TableConstants';
import {
  ClientIdentifiableTypedValue,
  JsonTypedValue,
  NumberTypedValue,
  StringTypedValue,
  UnitReferenceTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {BioentityContent, ReagentType, Source} from 'samples/plate-maps/PlateMapConstants';
import {
  MaybeMixedMaterialInputWithError,
  MaybeMixedMeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export const MULTIPLE_VALUES_SENTINEL = 'Multiple values';
export const NO_MATERIAL_INPUT_SENTINEL = 'NO_MATERIAL_INPUT_SENTINEL';

export type MaybeMixedValue<T> = T | null | typeof MULTIPLE_VALUES_SENTINEL;
export type MaybeMixedMeasurementValue = MaybeMixedValue<number>;
export type MaybeMixedMeasurementUnit = MaybeMixedValue<MeasurementUnit>;
export interface MaybeMixedMeasurement {
  value: MaybeMixedMeasurementValue;
  unit: MaybeMixedMeasurementUnit;
}

export type MaybeMixedPlateMaterialInputId = MaybeMixedValue<string>;

export interface SchemaData {
  entityIds: Set<string>;
  sourceConcentration: MaybeMixedMeasurementWithErrors;
  finalConcentration: MaybeMixedMeasurementWithErrors;
  materialInput?: MaybeMixedMaterialInputWithError;
}

export interface ReagentData {
  transferQuantity: MaybeMixedMeasurementWithErrors;
  type: ReagentType;
  schemas: {
    [schemaId: string]: SchemaData;
  };
}

/**
 * A map from 'schema grouping' (a deduped, sorted, concatenated string of tag schema API ids that co-occur in
 * the same container) to 1) transfer quantity data, which is stored per container, and 2) for each tag schema
 * API id in the container, a set of reagents with that schema ID in that schema grouping.
 */
export interface ReagentDataBySchemas {
  [schemaGroupingId: string]: ReagentData;
}

/**
 * A map from 'schema grouping' (a deduped, sorted, concatenated string of tag schema API ids that co-occur in
 * the same container) to structured table cells representing various aspects of reagent measurements.
 */
export interface ReagentMeasurementCellsBySchemas {
  [schemaGroupingId: string]: {
    transferQuantity: Cell;
    transferQuantityUnit: Cell;
    schemas: {
      [schemaId: string]: {
        finalConcentration: Cell;
        finalConcentrationUnit: Cell;
        sourceConcentration: Cell;
        sourceConcentrationUnit: Cell;
        materialInput?: Cell;
      };
    };
  };
}

/**
 * A map of numeric well index to structured table cells representing measurements to update on each well and
 * its contents and sources.
 */
export interface ReagentMeasurementCellsByWells {
  [wellIndex: number]: {
    contentCells: {
      [reagentId: string]: {
        finalConcentration: Cell;
        finalConcentrationUnit: Cell;
        materialInput?: Cell;
      };
    };
    sourceCells: {
      [containerOrContainableId: string]: {
        transferQuantity: Cell;
        transferQuantityUnit: Cell;
        /*
         * For rows representing a ContainerSource, one entry per content in that container, mapped by each
         * content's API ID; for rows representing a BioentitySource, one entry, mapped by that source's API
         * ID. BioentitySource aren't a true 'content' but this is cleaner than having different shapes for
         * different types of sources.
         */
        contents: {
          [containableId: string]: {
            finalConcentration: Cell;
            finalConcentrationUnit: Cell;
            materialInput?: Cell;
            sourceConcentration: Cell;
            sourceConcentrationUnit: Cell;
          };
        };
      };
    };
    finalQuantity: Cell;
    finalQuantityUnit: Cell;
  };
}

export enum FillPlateTransferTableViewMode {
  SUMMARY = 'SUMMARY',
  PLATE_RECORD = 'PLATE_RECORD',
  WELL = 'WELL',
}

export enum SharedTransferTableColumnKey {
  SOURCE_ICON = 'SOURCE_ICON',
  MATERIAL_INPUT = 'MATERIAL_INPUT',
  SOURCE_CONCENTRATION = 'SOURCE_CONCENTRATION',
  SOURCE_CONCENTRATION_UNIT = 'SOURCE_CONCENTRATION_UNIT',
  TRANSFER_QUANTITY = 'TRANSFER_QUANTITY',
  TRANSFER_QUANTITY_UNIT = 'TRANSFER_QUANTITY_UNIT',
  FINAL_CONCENTRATION = 'FINAL_CONCENTRATION',
  FINAL_CONCENTRATION_UNIT = 'FINAL_CONCENTRATION_UNIT',
}

export enum SummaryViewTransferTableColumnKey {
  SOURCE_SCHEMA = 'SOURCE_SCHEMA',
}

export enum DetailViewTransferTableColumnKey {
  WELL = 'WELL',
  SOURCE = 'SOURCE',
  FINAL_QUANTITY = 'FINAL_QUANTITY',
  FINAL_QUANTITY_UNIT = 'FINAL_QUANTITY_UNIT',
}

export enum PlateRecordViewTransferTableColumnKey {
  EXPERIMENTAL_ROLE = 'EXPERIMENTAL_ROLE',
  SOURCE_CONTAINER = 'SOURCE_CONTAINER',
}

export const CELL_FIELDS_TO_PICK: Array<keyof Cell> = ['text', 'typedValue', 'error'];

type BaseTransferTableCell = Pick<Cell, 'text' | 'typedValue' | 'error' | 'locked' | 'mergeRowSpan'>;

interface LockableStringTransferTableCell extends BaseTransferTableCell {
  typedValue?: StringTypedValue;
}

export type StringTransferTableCell = Omit<LockableStringTransferTableCell, 'locked'>;

interface LockableUnitReferenceTransferTableCell extends BaseTransferTableCell {
  typedValue?: UnitReferenceTypedValue;
}

interface LockableNumberTransferTableCell extends BaseTransferTableCell {
  typedValue?: NumberTypedValue;
}

interface LockableClientIdentifiableTransferTableCell extends BaseTransferTableCell {
  // Mild hack - this is going to be used for _server_ identifiables - but it strongly constrains the
  // typedValue to contain only type / id / name
  typedValue?: ClientIdentifiableTypedValue;
}

interface SourceIcon extends StringTypedValue {
  type: BaseTypedValueType.STRING;
  rawValue: BenchlingIconName;
}

interface SourceIconTransferTableCell extends StringTransferTableCell {
  typedValue: SourceIcon;
}

// The following cells will always be locked.
interface ExperimentalRoleJson extends JsonTypedValue {
  jsonValue: {
    experimentalRole: ExperimentalRole | null;
  };
}

interface ExperimentalRoleTransferTableCell extends Omit<BaseTransferTableCell, 'locked'> {
  typedValue: ExperimentalRoleJson;
}

interface SourceSchemaChipJson extends JsonTypedValue {
  jsonValue: {
    apiId: string | null;
    displayNameOverride: string;
    schemaColor: string | null;
  };
}

interface SourceSchemaChipTransferTableCell extends Omit<BaseTransferTableCell, 'locked'> {
  typedValue: SourceSchemaChipJson;
}

export interface SourceChipJson extends JsonTypedValue {
  jsonValue: {
    // Even if this isn't a Source, we want to pass the reagent in so we can tell the difference between
    // preexisting contents, sources, and empty wells
    reagent: Source | BioentityContent | null;
  };
}

interface SourceChipTransferTableCell extends Omit<BaseTransferTableCell, 'locked'> {
  typedValue: SourceChipJson;
}

interface AnonymousTagValueJson extends JsonTypedValue {
  jsonValue: {
    tag: MinimalSchemaTag | null;
  };
}

export interface AnonymousTagValueTransferTableCell extends Omit<BaseTransferTableCell, 'locked'> {
  typedValue: AnonymousTagValueJson;
}

export interface BaseFillPlateTransferTableRowContents<T extends FillPlateTransferTableViewMode> {
  viewMode: T;
  schemaId: string;
  [SharedTransferTableColumnKey.SOURCE_ICON]: SourceIconTransferTableCell;
  [SharedTransferTableColumnKey.MATERIAL_INPUT]?: LockableClientIdentifiableTransferTableCell;
  [SharedTransferTableColumnKey.SOURCE_CONCENTRATION]: LockableNumberTransferTableCell;
  [SharedTransferTableColumnKey.SOURCE_CONCENTRATION_UNIT]: LockableUnitReferenceTransferTableCell;
  [SharedTransferTableColumnKey.TRANSFER_QUANTITY]: LockableNumberTransferTableCell;
  [SharedTransferTableColumnKey.TRANSFER_QUANTITY_UNIT]: LockableUnitReferenceTransferTableCell;
  [SharedTransferTableColumnKey.FINAL_CONCENTRATION]: LockableNumberTransferTableCell;
  [SharedTransferTableColumnKey.FINAL_CONCENTRATION_UNIT]: LockableUnitReferenceTransferTableCell;
}

export interface SummaryViewRowContents extends BaseFillPlateTransferTableRowContents<FillPlateTransferTableViewMode.SUMMARY> {
  [SummaryViewTransferTableColumnKey.SOURCE_SCHEMA]: SourceSchemaChipTransferTableCell;
  viewMode: FillPlateTransferTableViewMode.SUMMARY;
  schemaGroupingId: string;
}

export interface SharedDetailViewRowContents<
  T extends FillPlateTransferTableViewMode.WELL | FillPlateTransferTableViewMode.PLATE_RECORD,
> extends BaseFillPlateTransferTableRowContents<T> {
  /** The flattened index of the well */
  wellIndex: number;
  /** Whether the reagent represented by this row is an existing content */
  isExistingContent: boolean;
  /** Whether the well is empty */
  isEmpty: boolean;
  /** For a container source, the container's API ID; otherwise, the API ID of the reagent represented by this row */
  containerOrContainableId: string;
  /** The API ID of the reagent represented by this row, if it is a containable */
  containableId: string | null;
  // Well name will always be locked
  [DetailViewTransferTableColumnKey.WELL]: StringTransferTableCell;
  [DetailViewTransferTableColumnKey.SOURCE]: SourceChipTransferTableCell;
  [DetailViewTransferTableColumnKey.FINAL_QUANTITY]: LockableNumberTransferTableCell;
  [DetailViewTransferTableColumnKey.FINAL_QUANTITY_UNIT]: LockableUnitReferenceTransferTableCell;
}

export interface WellViewRowContents extends SharedDetailViewRowContents<FillPlateTransferTableViewMode.WELL> {
  viewMode: FillPlateTransferTableViewMode.WELL;
}

export interface PlateRecordViewRowContents extends SharedDetailViewRowContents<FillPlateTransferTableViewMode.PLATE_RECORD> {
  viewMode: FillPlateTransferTableViewMode.PLATE_RECORD;
  schemaTagColumns: {[key: string]: AnonymousTagValueTransferTableCell};
  dataParameterColumns: {[key: string]: AnonymousTagValueTransferTableCell};
  [PlateRecordViewTransferTableColumnKey.EXPERIMENTAL_ROLE]: ExperimentalRoleTransferTableCell;
  [PlateRecordViewTransferTableColumnKey.SOURCE_CONTAINER]: SourceChipTransferTableCell;
}
